// Laden und Befehle eines Pflegeabschnitts -- der Ablauf, den Kennungen,
// Kontaktwege und Adressen bisher je einmal selbst gebaut hatten.
//
// Der Hook laedt den Abschnitt selbst und setzt die Befehle aus
// useSectionCommands darauf. Was auf dem Bildschirm steht, entscheidet der
// Bildschirm; wann welcher Zustand gilt, steht hier und dort.
//
// DIE REGELN des Ladens (die der Befehle stehen in use-section-commands.ts):
//   * Nach einem Befehl laedt der Abschnitt still nach und zeigt, was JETZT gilt.
//   * Im PROBEMODUS (nach einem abgebrochenen Anlegen) aendert nur ein Erfolg
//     die Liste. Sonst ersetzte die Fehlerlage den Bildschirm, und die stehende
//     Eingabe samt "Erneut versuchen" verschwaende.
//   * Ein ausdrueckliches Neuladen raeumt die alte Meldung ab; sonst
//     behauptete der wiederhergestellte Bildschirm weiter einen Konflikt.
//   * Eine spaete Antwort eines ueberholten Laufs aendert nichts.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

import type { PlainMessage } from '../access.ts';
import type { FieldError } from '../command-client.ts';
import {
  UNREACHABLE_MESSAGE,
  useSectionCommands,
  type CommandSpec,
  type CreateMessages,
  type CreateSpec,
} from './use-section-commands.ts';

export type SectionStatus = 'laedt' | 'geladen' | 'unbekannt' | 'abgelehnt';

export type SectionLoadResult<Data> =
  | { readonly outcome: 'loaded'; readonly data: Data }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export interface MaintainedSectionOptions<Data> {
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  /** Laedt den Abschnitt mit einem gueltigen Token. */
  readonly fetchSection: (token: string) => Promise<SectionLoadResult<Data>>;
  /**
   * Wechselt dieser Schluessel, laedt der Abschnitt neu -- etwa mit einem
   * anderen Kontakt. `fetchSection` selbst darf je Rerender neu entstehen.
   */
  readonly sectionKey: string;
  /** Die Saetze des Fachmoduls zum Anlegen (ADR-0060); ein Aufruf von `create` darf eigene mitgeben. */
  readonly createMessages: CreateMessages;
  /**
   * Der Satz nach einem ausdruecklichen Neuladen, das gelang -- etwa "Die
   * Beziehungen zeigen jetzt den aktuellen Stand." Ohne ihn bliebe die
   * Statusregion nach dem Knopf stumm, und wer nicht sieht, wuesste nicht, ob
   * etwas geschah (WCAG 4.1.3). Das stille Nachladen nach einem Befehl sagt
   * nichts an: dort spricht der Befehl.
   */
  readonly reloadedAnnouncement?: string;
}

/** Die Ueberschrift der Ansage nach einem ausdruecklichen Neuladen. */
export const RELOADED_TITLE = 'Neu geladen';

export interface MaintainedSection<Data> {
  readonly status: SectionStatus;
  readonly data: Data | undefined;
  readonly message: PlainMessage | undefined;
  /** Siehe SectionCommands.messageOffen. */
  readonly messageOffen: boolean;
  readonly confirmation: string | undefined;
  readonly confirmationTitle: string | undefined;
  readonly fieldErrors: readonly FieldError[];
  readonly busy: boolean;
  /** Das Anlegen blieb ohne Antwort oder scheiterte an einer Stoerung -- dann steht "Erneut versuchen" da. */
  readonly submitFailed: boolean;
  /** Ein Befehl mit `retryAfterAbort` blieb ohne Antwort; "Erneut versuchen" sendet ihn noch einmal. */
  readonly retryCommand: (() => void) | undefined;
  /** Der zugaengliche Name dazu, falls der Befehl einen mitgab. */
  readonly retryLabel: string | undefined;
  /** Das Ziel der Fokusfuehrung; gehoert an die EINE Region mit aria-live. */
  readonly statusRef: RefObject<HTMLDivElement | null>;
  /** Der Rahmen des Abschnitts; dort sucht die Fokusfuehrung nach einer Feldablehnung das erste markierte Feld. */
  readonly sectionRef: RefObject<HTMLDivElement | null>;
  /** Ausdrueckliches Neuladen: Spinner, alte Meldung weg. */
  readonly reload: () => void;
  readonly command: <Applied>(spec: CommandSpec<Applied>) => Promise<void>;
  readonly create: <Applied>(spec: CreateSpec<Applied>) => Promise<void>;
  /** Das Ergebnis einer Handlung, die kein Befehl ist -- etwa einer Suche im Abschnitt. */
  readonly showMessage: (message: PlainMessage | undefined) => void;
  /** Alles, was der letzte Befehl hinterliess, weg, wenn eine solche Handlung beginnt. */
  readonly clearOutcome: () => void;
}

export function useMaintainedSection<Data>(options: MaintainedSectionOptions<Data>): MaintainedSection<Data> {
  const { accessToken, onReauthenticate, sectionKey } = options;
  const [data, setData] = useState<Data | undefined>(undefined);
  const [status, setStatus] = useState<SectionStatus>('laedt');

  const fetchRef = useRef(options.fetchSection);
  // Erst nach dem Uebernehmen eines Renders, nicht waehrend: ein verworfener
  // Render duerfte sonst einen Abruf fuer einen nicht angezeigten Kontakt
  // hinterlassen. Layout-Effekte laufen vor dem Ladeeffekt unten.
  useLayoutEffect(() => {
    fetchRef.current = options.fetchSection;
  });
  const laufRef = useRef(0);

  // `load` entsteht unten und braucht die Meldungen der Befehle; die Befehle
  // brauchen `load` zum Nachladen. Der Ref loest den Kreis ohne Rerender.
  const loadRef = useRef<(silent: boolean, probe: boolean) => Promise<boolean>>(() => Promise.resolve(false));
  const befehle = useSectionCommands({
    accessToken,
    onReauthenticate,
    // Ein Fehlschlag meldet `load` selbst (ausser im Probemodus); `false` sagt dem Hook nur, dass nichts geladen ist.
    refresh: async (probe) => ((await loadRef.current(true, probe)) ? undefined : false),
    createMessages: options.createMessages,
  });
  const { showMessage, showConfirmation, clearMessages, focusStatus } = befehle;
  const ansageRef = useRef(options.reloadedAnnouncement);
  useLayoutEffect(() => {
    ansageRef.current = options.reloadedAnnouncement;
  });

  /**
   * `silent`: nach einem Befehl, ohne Spinner. `probe`: nach einem Abbruch, nur
   * ein Erfolg zaehlt. `announce`: ausdruecklich neu geladen, ein Erfolg wird angesagt.
   */
  const load = useCallback(
    async (silent = false, probe = false, announce = false): Promise<boolean> => {
      const meinLauf = laufRef.current + 1;
      laufRef.current = meinLauf;
      const gueltig = (): boolean => laufRef.current === meinLauf;
      if (!silent) {
        setStatus('laedt');
        clearMessages();
      }
      try {
        const token = await accessToken();
        if (!gueltig()) {
          return false;
        }
        if (token === undefined) {
          if (!probe) {
            onReauthenticate();
          }
          return false;
        }
        const result = await fetchRef.current(token);
        if (!gueltig()) {
          return false;
        }
        if (probe && result.outcome !== 'loaded') {
          return false;
        }
        if (result.outcome === 'not-found') {
          setStatus('unbekannt');
          return false;
        }
        if (result.outcome === 'rejected') {
          showMessage(result.message);
          setStatus('abgelehnt');
          return false;
        }
        setData(result.data);
        setStatus('geladen');
        if (announce && ansageRef.current !== undefined) {
          showConfirmation(ansageRef.current, RELOADED_TITLE);
        }
        return true;
      } catch {
        if (!gueltig() || probe) {
          return false;
        }
        showMessage(UNREACHABLE_MESSAGE);
        setStatus('abgelehnt');
        return false;
      }
    },
    // sectionKey steht fuer alles, was fetchSection anfragt; fetchSection
    // selbst liegt im Ref und darf je Rerender neu entstehen.
    [accessToken, onReauthenticate, sectionKey, clearMessages, showMessage, showConfirmation],
  );
  useLayoutEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    void load();
    return () => {
      laufRef.current += 1;
    };
  }, [load]);

  // Der Knopf, der neu laedt, verschwindet mit dem Laden. Ohne Ziel fiele der
  // Fokus auf das Dokument (WCAG 2.4.3); die Statusregion bleibt stehen und
  // traegt Spinner wie Ergebnis. Das stille Nachladen nach einem Befehl
  // fordert nichts an -- der Befehl hat den Fokus schon hingelegt.
  const reload = useCallback(() => {
    focusStatus();
    void load(false, false, true);
  }, [focusStatus, load]);

  return {
    status,
    data,
    message: befehle.message,
    messageOffen: befehle.messageOffen,
    confirmation: befehle.confirmation,
    confirmationTitle: befehle.confirmationTitle,
    fieldErrors: befehle.fieldErrors,
    busy: befehle.busy,
    submitFailed: befehle.submitFailed,
    retryCommand: befehle.retryCommand,
    retryLabel: befehle.retryLabel,
    statusRef: befehle.statusRef,
    sectionRef: befehle.sectionRef,
    reload,
    command: befehle.command,
    create: befehle.create,
    showMessage,
    clearOutcome: befehle.clearOutcome,
  };
}
